import DeleteIcon from "@mui/icons-material/Delete";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import PeopleIcon from "@mui/icons-material/People";
import SaveIcon from "@mui/icons-material/Save";
import { Chip, Grid, Tooltip, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import * as AYSDialog from "components/UI/AYSDialog";
import {
    BranchDataProps,
    HandleBranchProps,
} from "components/UI/AcxRecursiveTree/AcxRecursiveTreeBranch";
import { observer } from "mobx-react";
import React from "react";
import { AuthStore } from "stores/AuthStore";
import { Tier } from "../../types/Tier.type";
import AddRowIcon from "./Components/AddRowIcon";
import EditTierIcon from "./Components/EditTierIcon";
import SamplingTargetLabeledMetrics from "./Components/SamplingTargetLabeledMetrics";
import { OrganizationServiceHierarchyStore } from "./stores/OrganizationServiceHierarchyStore";
import {
    ExistingTierMatch,
    findExistingTierPaths,
} from "./utils/findExistingTierPath";
import theme from "Theme/AppTheme";

interface OrganizationServiceHierarchyTierProps {
    branch: BranchDataProps;
    handleTier: HandleBranchProps;
    store: OrganizationServiceHierarchyStore;
    authStore: AuthStore;
}

const OrganizationServiceHierarchyTier: React.FC<OrganizationServiceHierarchyTierProps> =
    observer(({ branch, handleTier, store, authStore }) => {
        const [name, setName] = React.useState<string>(branch.name ?? "");
        const [isLoading, setIsLoading] = React.useState<boolean>(false);
        const [showRenameDialog, setShowRenameDialog] =
            React.useState<boolean>(false);
        const [duplicateMatches, setDuplicateMatches] = React.useState<
            ExistingTierMatch[]
        >([]);

        const tier = branch as Tier;
        const hasEditPerms = authStore.canUserEdit("Service Hierarchy");
        const isMissingTherapeuticAreas =
            (tier.therapeuticAreas?.length ?? 0) === 0;

        React.useEffect(() => {
            setName(branch.name ?? "");
        }, [branch.name]);

        const trimmedName = name.trim();
        const isNameDirty =
            trimmedName.length > 0 && trimmedName !== branch.name;

        const performRename = async () => {
            setIsLoading(true);
            await handleTier({ ...branch, name: trimmedName }, "Update");
            setIsLoading(false);
        };

        const handleSaveName = async () => {
            if (!isNameDirty || isLoading) return;
            const matches = await findExistingTierPaths(trimmedName, branch.id);
            if (matches.length > 0) {
                setDuplicateMatches(matches);
                return;
            }
            setShowRenameDialog(true);
        };

        const handleConfirmRename = async () => {
            setShowRenameDialog(false);
            await performRename();
        };

        const handleSaveAnywayRename = async () => {
            setDuplicateMatches([]);
            await performRename();
        };

        const handleDelete = () => {
            store.setActiveTier(branch as Tier);
            store.setShowDeleteDialog(true);
        };

        const handleTierReassignment = () => {
            store.setActiveTier(branch as Tier);
            store.setShowReassignTierDialog(true);
        };

        React.useEffect(() => {
            if (store.endpointFired.active && store.activeTier) {
                if (branch.id === store.activeTier.id) {
                    switch (store.endpointFired.payload) {
                        case "Add":
                            handleTier(
                                store.activeTier as BranchDataProps,
                                "Add",
                            );

                            return;

                        case "Update":
                            handleTier(
                                store.activeTier as BranchDataProps,
                                "Update",
                            );

                            return;

                        case "Remove":
                            handleTier(
                                store.activeTier as BranchDataProps,
                                "Remove",
                            );

                            return;

                        default:
                            return;
                    }
                }
            }
        }, [
            store.endpointFired,
            store.endpointFired.active,
            store.activeTier,
            branch.id,
            handleTier,
        ]);

        return (
            <>
                <Grid container item xs={4} alignItems="center">
                    <AcxMainTextField
                        isDisabled={!hasEditPerms}
                        containerStyle={{
                            width: "100%",
                            padding: "0.25rem 1rem",
                        }}
                        fullWidth
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveName();
                            }
                        }}
                        labelText={
                            store.orgServiceSetup?.[branch.branchDepth ?? 0] ??
                            ""
                        }
                        endAdornment={
                            hasEditPerms && isNameDirty ? (
                                <Tooltip title="Save Name">
                                    <SaveIcon
                                        color={
                                            isLoading ? "disabled" : "primary"
                                        }
                                        onClick={
                                            isLoading
                                                ? undefined
                                                : handleSaveName
                                        }
                                        sx={{
                                            cursor: isLoading
                                                ? "not-allowed"
                                                : "pointer",
                                        }}
                                    />
                                </Tooltip>
                            ) : undefined
                        }
                    />
                </Grid>

                <Grid
                    container
                    item
                    xs={4}
                    alignItems="center"
                    justifyContent="center"
                >
                    <SamplingTargetLabeledMetrics
                        tier={branch as Tier}
                        osmStore={store}
                    />
                </Grid>

                <Grid
                    container
                    item
                    xs={4}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {isLoading && (
                        <Grid item>
                            <AcxLoadingIndicator
                                size={25}
                                alternate="PuffLoader"
                            />
                        </Grid>
                    )}

                    {isMissingTherapeuticAreas && (
                        <Chip
                            label="Missing Info"
                            size="small"
                            color="warning"
                            sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                marginRight: theme.spacing(1),
                            }}
                        />
                    )}

                    {(branch.isLeaf ?? false) && (
                        <Tooltip title="View Assigned Users">
                            <PeopleIcon
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginRight: theme.spacing(1),
                                    marginLeft: theme.spacing(1),
                                    cursor: "pointer",
                                }}
                                htmlColor="#bebebe"
                                onClick={() => {
                                    store.setActiveTier(branch as Tier);
                                    store.openServiceHierarchyUsersDrawer();
                                }}
                            />
                        </Tooltip>
                    )}

                    {hasEditPerms && (
                        <AddRowIcon
                            tier={branch as Tier}
                            osmStore={store}
                            isLeaf={branch?.isLeaf ?? false}
                        />
                    )}

                    {(branch.branchDepth ?? 0) > 0 && hasEditPerms && (
                        <Tooltip title="Reassign Tier">
                            <ImportExportIcon
                                color="primary"
                                onClick={handleTierReassignment}
                                cursor="pointer"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginRight: theme.spacing(1),
                                    marginLeft: theme.spacing(1),
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                    )}

                    <EditTierIcon
                        tier={branch}
                        osmStore={store}
                        isLeaf={branch?.isLeaf ?? false}
                    />

                    {hasEditPerms && (
                        <Tooltip title="Delete Tier">
                            <DeleteIcon
                                htmlColor="#bebebe"
                                onClick={handleDelete}
                                cursor="pointer"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginRight: theme.spacing(1),
                                    marginLeft: theme.spacing(1),
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                    )}
                </Grid>

                <AYSDialog.Container
                    isOpen={showRenameDialog}
                    onClose={() => setShowRenameDialog(false)}
                    id={`rename-tier-${branch.id}`}
                >
                    <AYSDialog.Title>Confirm rename</AYSDialog.Title>
                    <AYSDialog.Subtitle>
                        Renaming a tier will update it everywhere it's
                        referenced.
                    </AYSDialog.Subtitle>
                    <AYSDialog.Content>
                        <Typography variant="body2">
                            "{branch.name}" → "{trimmedName}"
                        </Typography>
                    </AYSDialog.Content>
                    <AYSDialog.Actions>
                        <AcxButton
                            color="white"
                            onClick={() => setShowRenameDialog(false)}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={handleConfirmRename}
                        >
                            Save
                        </AcxButton>
                    </AYSDialog.Actions>
                </AYSDialog.Container>

                <AYSDialog.Container
                    isOpen={duplicateMatches.length > 0}
                    onClose={() => setDuplicateMatches([])}
                    id={`rename-duplicate-${branch.id}`}
                >
                    <AYSDialog.Title>
                        The name "{trimmedName}" is already in use
                    </AYSDialog.Title>
                    <AYSDialog.Content>
                        {duplicateMatches.map((match, idx) => (
                            <Typography
                                key={`${match.path.join("/")}-${idx}`}
                                variant="body2"
                            >
                                {match.path.join(" → ")}
                            </Typography>
                        ))}
                    </AYSDialog.Content>
                    <AYSDialog.Subtitle>
                        You can still save, but multiple hierarchies will share
                        this name. This may cause confusion in reporting and
                        downstream workflows.
                    </AYSDialog.Subtitle>
                    <AYSDialog.Actions>
                        <AcxButton
                            color="white"
                            onClick={() => setDuplicateMatches([])}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={handleSaveAnywayRename}
                        >
                            Save Anyway
                        </AcxButton>
                    </AYSDialog.Actions>
                </AYSDialog.Container>
            </>
        );
    });

export default OrganizationServiceHierarchyTier;
